import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchPrintletOrders } from "../../api/PrintletAPIs";
import {
  Search,
  Filter,
  Package,
  Truck,
  Wallet,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Phone,
  User,
  ExternalLink,
  Inbox,
} from "lucide-react";

// ─── Status Badge Config ───────────────────────────────────────────
const STATUS_CONFIG = {
  Preparing:            { label: "قيد التجهيز",       color: "bg-slate-100 text-slate-700 border-slate-200",   dot: "bg-slate-400" },
  ArtworkProcessing:    { label: "معالجة التصميم",    color: "bg-blue-50 text-blue-700 border-blue-200",       dot: "bg-blue-500" },
  ValidationFailed:     { label: "فشل التحقق",        color: "bg-rose-50 text-rose-700 border-rose-200",       dot: "bg-rose-500" },
  Quoted:               { label: "تم التسعير",         color: "bg-amber-50 text-amber-700 border-amber-200",    dot: "bg-amber-500" },
  PendingVerification:  { label: "بانتظار التحقق",    color: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  PaymentRequired:      { label: "مطلوب دفع",          color: "bg-red-50 text-red-700 border-red-200",          dot: "bg-red-500" },
  Accepted:             { label: "مقبول",              color: "bg-teal-50 text-teal-700 border-teal-200",       dot: "bg-teal-500" },
  InProduction:         { label: "قيد الإنتاج",        color: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  Shipped:              { label: "تم الشحن",           color: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  Delivered:            { label: "تم التسليم",         color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  Failed:               { label: "فشل",                color: "bg-rose-50 text-rose-700 border-rose-200",       dot: "bg-rose-500" },
  Cancelled:            { label: "ملغي",               color: "bg-slate-100 text-slate-500 border-slate-200",   dot: "bg-slate-400" },
};

const FILTER_STATUSES = Object.keys(STATUS_CONFIG);

// ─── Sub Components ────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || {
    label: status || "—",
    color: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, accent = "slate" }) {
  const accents = {
    slate:   "bg-slate-50 text-slate-600 border-slate-200",
    indigo:  "bg-indigo-50 text-indigo-600 border-indigo-200",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
    amber:   "bg-amber-50 text-amber-600 border-amber-200",
  };
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
      <div className={`p-2 rounded-lg border ${accents[accent]}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 truncate">{label}</p>
        <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="p-4 rounded-full bg-slate-100 mb-3">
        <Inbox size={32} className="text-slate-400" />
      </div>
      <h3 className="font-bold text-slate-700 mb-1">لا توجد طلبات</h3>
      <p className="text-sm text-slate-500 max-w-xs">
        لم يتم العثور على طلبات Printlet مطابقة لمعايير البحث الحالية.
      </p>
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className="p-3">
          <div className="h-4 bg-slate-100 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────
export default function PrintletOrdersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["printletOrders", search, status, page],
    queryFn: () => fetchPrintletOrders({ search, status, page, pageSize }),
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasActiveFilters = search || status;

  // ─── Quick stats from current page ───────────────────────────────
  const stats = useMemo(() => {
    const shipped = items.filter((r) => r.status === "Shipped").length;
    const delivered = items.filter((r) => r.status === "Delivered").length;
    const codTotal = items.reduce((sum, r) => sum + (Number(r.codCollectionAmount) || 0), 0);
    return { shipped, delivered, codTotal };
  }, [items]);

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPage(1);
  };

  return (
    <div className="space-y-5">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Package size={22} className="text-indigo-600" />
            طلبات Printlet
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            متابعة حالة التنفيذ الخارجي لكل طلب — منفصل عن حالة جراباتكم
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 transition"
        >
          <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
          تحديث
        </button>
      </div>

      {/* ─── Stats Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Package}  label="إجمالي الطلبات"  value={total}                  accent="indigo" />
        <StatCard icon={Truck}    label="تم الشحن (صفحة)"  value={stats.shipped}           accent="emerald" />
        <StatCard icon={Package}  label="تم التسليم (صفحة)" value={stats.delivered}         accent="emerald" />
        <StatCard icon={Wallet}   label="إجمالي COD (صفحة)" value={stats.codTotal.toLocaleString()} accent="amber" />
      </div>

      {/* ─── Filters ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3">
        <div className="flex flex-wrap gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="بحث برقم جراباتكم / Printlet / هاتف / تتبع..."
              className="w-full border border-slate-200 rounded-xl pr-9 pl-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <Filter size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="border border-slate-200 rounded-xl pr-9 pl-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition min-w-[180px]"
            >
              <option value="">كل الحالات</option>
              {FILTER_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
              ))}
            </select>
          </div>

          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition"
            >
              <X size={15} />
              مسح
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
            {search && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium">
                بحث: {search}
                <button onClick={() => { setSearch(""); setPage(1); }}><X size={12} /></button>
              </span>
            )}
            {status && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium">
                الحالة: {STATUS_CONFIG[status]?.label || status}
                <button onClick={() => { setStatus(""); setPage(1); }}><X size={12} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ─── Table ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                <th className="p-3 text-right font-semibold">جراباتكم</th>
                <th className="p-3 text-right font-semibold">العميل</th>
                <th className="p-3 text-right font-semibold">حالة الطلب</th>
                <th className="p-3 text-right font-semibold">Printlet</th>
                <th className="p-3 text-right font-semibold">تتبع</th>
                <th className="p-3 text-right font-semibold">COD</th>
                <th className="p-3 text-right font-semibold">تكلفة Printlet</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && !data ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState />
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/printlet/orders/${row.id}`)}
                    className="border-b border-slate-100 hover:bg-indigo-50/40 cursor-pointer transition-colors"
                  >
                    {/* Order ID */}
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5 font-bold text-slate-800">
                        <span className="text-indigo-600">#</span>
                        {row.girabatOrderId}
                      </span>
                    </td>

                    {/* Customer */}
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 text-slate-800">
                        <User size={13} className="text-slate-400" />
                        {row.customerName || "—"}
                      </div>
                      {row.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5" dir="ltr">
                          <Phone size={11} className="text-slate-400" />
                          {row.phone}
                        </div>
                      )}
                    </td>

                    {/* Girabat status */}
                    <td className="p-3">
                      <span className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-600 font-medium">
                        {row.girabatStatus || "—"}
                      </span>
                    </td>

                    {/* Printlet status */}
                    <td className="p-3">
                      <StatusBadge status={row.status} />
                      {row.printletOrderId && (
                        <div className="text-xs text-slate-400 mt-1 font-mono truncate max-w-[140px]" title={row.printletOrderId}>
                          {row.printletOrderId}
                        </div>
                      )}
                    </td>

                    {/* Tracking */}
                    <td className="p-3">
                      {row.trackingNumber ? (
                        <div className="flex items-center gap-1.5 text-xs text-slate-700">
                          <Truck size={13} className="text-violet-500" />
                          <span className="font-mono" dir="ltr">{row.trackingNumber}</span>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    {/* COD */}
                    <td className="p-3">
                      {row.codCollectionAmount != null ? (
                        <span className="font-semibold text-amber-700">
                          {Number(row.codCollectionAmount).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    {/* Printlet total */}
                    <td className="p-3">
                      {row.printletTotalAmount != null ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-800">
                            {Number(row.printletTotalAmount).toLocaleString()}
                          </span>
                          <span className="text-xs text-slate-500">
                            {row.printletTotalCurrency || ""}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Footer / Pagination ─────────────────────────────── */}
        {!isLoading && items.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              عرض <b className="text-slate-700">{(page - 1) * pageSize + 1}</b> –{" "}
              <b className="text-slate-700">{Math.min(page * pageSize, total)}</b> من{" "}
              <b className="text-slate-700">{total.toLocaleString()}</b>
            </div>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={15} />
                السابق
              </button>
              <span className="px-3 py-1.5 text-sm text-slate-600">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                التالي
                <ChevronLeft size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}